import { inject, Injectable, signal, Signal } from '@angular/core';
import { LocalStorageKeys, LocalStorageService } from '../data/local-storage.service';
import { Language, languageValues } from '@concretecode/shared';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  readonly #localStorageService = inject(LocalStorageService);
  readonly #language = signal<Language>((this.#localStorageService.get(LocalStorageKeys.preferredLanguage) as Language) || 'en');
  readonly #translationsCache: Record<string, Record<string, string>> = {};
  readonly #currentTranslations = signal<Record<string, string>>({});

  public initialize(): void {
    const stored = this.#localStorageService.get(LocalStorageKeys.preferredLanguage) as Language | null;
    if (!stored) {
      const browserLang = (typeof navigator !== 'undefined' ? navigator.language : null) ?? 'en';
      const detected: Language = languageValues.find((lang) => browserLang.toLowerCase().startsWith(lang)) ?? 'en';
      this.#language.set(detected);
    }
    const lang = this.#language();
    const translations = this.#translationsCache[lang] ?? this.#translationsCache['en'] ?? {};
    if (!this.#translationsCache[lang]) {
      this.#language.set('en');
    }
    this.#currentTranslations.set(translations);
  }

  public setLanguage(lang: Language): void {
    this.#language.set(lang);
    this.#localStorageService.set(LocalStorageKeys.preferredLanguage, lang);
    this.#currentTranslations.set(this.#translationsCache[lang] ?? {});
  }

  public getLanguage(): Signal<Language> {
    return this.#language.asReadonly();
  }

  public registerTranslations(lang: Language, translations: Record<string, string>): void {
    this.#translationsCache[lang] = translations;
  }

  public get(key: string): string {
    const value = this.#currentTranslations()[key];
    if (value === null || value === undefined) {
      return key;
    }
    return value;
  }

  public getWithParams(key: string, params?: Record<string, string | number>): string {
    return this.#interpolate(this.get(key), params);
  }

  public getPlural(key: string, count: number, params?: Record<string, string | number>): string {
    const category = new Intl.PluralRules(this.#language()).select(count);
    const suffix = category === 'other' ? 'more' : category;
    const translations = this.#currentTranslations();
    const value = translations[`${key}.${suffix}`] ?? translations[`${key}.more`];
    if (value === null || value === undefined) {
      return key;
    }
    return this.#interpolate(value, { count, ...params });
  }

  #interpolate(value: string, params?: Record<string, string | number>): string {
    if (!params) {
      return value;
    }

    let result = value;
    for (const [paramKey, paramValue] of Object.entries(params)) {
      result = result.split(`{{${paramKey}}}`).join(paramValue.toString());
    }
    return result;
  }
}
